/* @/slash autocomplete for script references and commands */

import { state, refs, esc, fileIconSvg } from './state.js';

let acActive = false;
let acStart = -1;
let acIndex = 0;
let acCandidates = [];
let acMode = ""; // "at" or "slash"

// ---------------------------------------------------------------------------
// Slash commands (static list — matches rule_router.py SLASH_COMMANDS)
// ---------------------------------------------------------------------------

const SLASH_COMMANDS = [
  { command: "/help",         description: "Show help and available commands" },
  { command: "/commands",     description: "List all slash commands" },
  { command: "/context",      description: "Show active quote/product/configuration IDs" },
  { command: "/sync",         description: "Compare and sync local files with cloud" },
  { command: "/preview",      description: "Open live template preview" },
  { command: "/traces",       description: "Fetch latest Developer Console traces" },
  { command: "/analyze",      description: "Analyze trace data — explain what scripts ran" },
  { command: "/clear-traces", description: "Clear accumulated trace data" },
  { command: "/exit",         description: "Exit the chat session" },
];

// ---------------------------------------------------------------------------
// @ script file references
// ---------------------------------------------------------------------------

function getAllScriptFiles() {
  const results = [];
  function walk(node) {
    if (!node) return;
    if (node.type === "file" && node.language === "python" && node.path.startsWith("global-scripts/")) {
      const name = node.name.replace(/\.py$/, "");
      results.push({ name, path: node.path, filename: node.name });
    }
    if (node.children) node.children.forEach(walk);
  }
  walk(state.fileTree);
  return results;
}

function atFilter(query) {
  const all = getAllScriptFiles();
  if (!query) return all.slice(0, 15);
  const q = query.toLowerCase();
  return all.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 15);
}

function slashFilter(query) {
  if (!query) return SLASH_COMMANDS.slice();
  const q = query.toLowerCase();
  return SLASH_COMMANDS.filter((c) => c.command.toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// Shared UI
// ---------------------------------------------------------------------------

function _getAcEl() { return refs.atAutocomplete || document.getElementById('atAutocomplete'); }
function _getInputEl() { return refs.promptInput || document.getElementById('promptInput'); }

function acShow() {
  acActive = true;
  const el = _getAcEl();
  if (el) el.hidden = false;
  // Mark the input as an expanded combobox controlling the suggestion listbox.
  const input = _getInputEl();
  if (input) {
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-controls", "atAutocomplete");
    input.setAttribute("aria-expanded", "true");
  }
}

function acHide() {
  acActive = false;
  acIndex = 0;
  acMode = "";
  const el = _getAcEl();
  if (el) { el.hidden = true; el.innerHTML = ""; }
  // Restore the plain textbox semantics (combobox is only active while open).
  const input = _getInputEl();
  if (input) {
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    input.removeAttribute("role");
    input.removeAttribute("aria-autocomplete");
    input.removeAttribute("aria-controls");
  }
}

function getAcQuery() {
  if (acStart < 0) return "";
  const input = _getInputEl();
  if (!input) return "";
  const text = input.value;
  const cursor = input.selectionStart;
  return text.substring(acStart + 1, cursor);
}

function acRender() {
  const acEl = _getAcEl();
  if (!acEl) return;
  acEl.innerHTML = "";
  const input = _getInputEl();
  if (acCandidates.length === 0) {
    const label = acMode === "slash" ? "No matching commands" : "No matching scripts";
    acEl.innerHTML = `<div class="at-empty">${label}</div>`;
    if (input) input.removeAttribute("aria-activedescendant");
    return;
  }
  for (let i = 0; i < acCandidates.length; i++) {
    const c = acCandidates[i];
    const div = document.createElement("div");
    div.className = `at-item${i === acIndex ? " active" : ""}`;
    // Expose each row as an ARIA listbox option; the active one is aria-selected.
    div.id = `ac-opt-${i}`;
    div.setAttribute("role", "option");
    div.setAttribute("aria-selected", i === acIndex ? "true" : "false");

    if (acMode === "slash") {
      // Slash command rendering
      const query = getAcQuery();
      let cmdHtml = esc(c.command);
      if (query) {
        const idx = c.command.toLowerCase().indexOf(query.toLowerCase());
        if (idx >= 0) {
          cmdHtml = esc(c.command.substring(0, idx)) +
            `<mark>${esc(c.command.substring(idx, idx + query.length))}</mark>` +
            esc(c.command.substring(idx + query.length));
        }
      }
      div.innerHTML = `
        <span class="at-item-icon" style="font-size:14px;opacity:0.5">/</span>
        <span class="at-item-name">${cmdHtml}</span>
        <span class="at-item-path">${esc(c.description)}</span>
      `;
    } else {
      // @ file reference rendering
      const query = getAcQuery();
      let nameHtml = esc(c.name);
      if (query) {
        const idx = c.name.toLowerCase().indexOf(query.toLowerCase());
        if (idx >= 0) {
          nameHtml = esc(c.name.substring(0, idx)) +
            `<mark>${esc(c.name.substring(idx, idx + query.length))}</mark>` +
            esc(c.name.substring(idx + query.length));
        }
      }
      div.innerHTML = `
        <span class="at-item-icon">${fileIconSvg("python")}</span>
        <span class="at-item-name">${nameHtml}</span>
        <span class="at-item-path">${esc(c.path)}</span>
      `;
    }
    div.addEventListener("mousedown", (e) => {
      e.preventDefault();
      acSelect(i);
    });
    acEl.appendChild(div);
  }
  // Move the combobox's active-descendant to the highlighted option.
  if (input) input.setAttribute("aria-activedescendant", `ac-opt-${acIndex}`);
}

function acSelect(index) {
  const c = acCandidates[index];
  if (!c) { acHide(); return; }
  const input = _getInputEl();
  if (!input) { acHide(); return; }
  const text = input.value;
  const cursor = input.selectionStart;
  const before = text.substring(0, acStart);
  const after = text.substring(cursor);

  let insert;
  if (acMode === "slash") {
    insert = c.command + " ";
  } else {
    insert = `@${c.name} `;
  }

  input.value = before + insert + after;
  input.selectionStart = input.selectionEnd = acStart + insert.length;
  input.focus();
  input.dispatchEvent(new Event("input"));
  acHide();
}

// ---------------------------------------------------------------------------
// Input handler — detect @ or / trigger
// ---------------------------------------------------------------------------

export function handleAtInput() {
  const input = _getInputEl();
  if (!input) return;
  const text = input.value;
  const cursor = input.selectionStart;

  // Check for / at start of input (slash commands)
  if (text.startsWith("/") && cursor > 0) {
    const query = text.substring(0, cursor);
    // Only trigger if no space before cursor (still typing the command)
    if (!/\s/.test(query)) {
      acStart = 0;
      acMode = "slash";
      // filter by everything after "/"
      acCandidates = slashFilter(query.substring(1));
      acIndex = 0;
      acShow();
      acRender();
      return;
    }
  }

  // Check for @ (file references)
  let foundAt = -1;
  for (let i = cursor - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "@") { foundAt = i; break; }
    if (ch === " " || ch === "\n") break;
  }

  if (foundAt >= 0) {
    acStart = foundAt;
    acMode = "at";
    const query = text.substring(foundAt + 1, cursor);
    if (/^\w*$/.test(query)) {
      acCandidates = atFilter(query);
      acIndex = 0;
      acShow();
      acRender();
      return;
    }
  }

  if (acActive) acHide();
}

// ---------------------------------------------------------------------------
// Keyboard navigation
// ---------------------------------------------------------------------------

export function handleAtKeydown(e) {
  if (!acActive) return false;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    acIndex = Math.min(acIndex + 1, acCandidates.length - 1);
    acRender();
    return true;
  }
  if (e.key === "ArrowUp") {
    e.preventDefault();
    acIndex = Math.max(acIndex - 1, 0);
    acRender();
    return true;
  }
  if (e.key === "Enter" || e.key === "Tab") {
    if (acCandidates.length > 0) {
      e.preventDefault();
      acSelect(acIndex);
      return true;
    }
  }
  if (e.key === "Escape") {
    e.preventDefault();
    acHide();
    return true;
  }
  return false;
}

export function hideAutocomplete() {
  setTimeout(acHide, 150);
}
